import React from 'react';
import { PropTypes } from 'prop-types';
import { Card, CardContent, Typography } from '@mui/material';
import Chart from 'react-apexcharts';

// Customer-health heatmap: rows = clients, columns = metrics. Color is driven
// by a per-column normalized value (0-100) so metrics on different scales stay
// comparable; the raw value is shown in the tooltip.
// `series` = [{ name: clientName, data: [{ x: metricLabel, y: normalized, raw }] }].

// Sequential single-hue scale (light -> dark) anchored on the Doctivity teal, so the
// legend reads as an intensity ramp. Zero keeps its own neutral swatch, since "no
// activity" is a different statement than "a little activity".
const INTENSITY_RANGES = [
  { from: 0, to: 0, color: '#EFEFEF', name: 'None' },
  { from: 1, to: 20, color: '#D6EDE7', name: '1-20%' },
  { from: 21, to: 40, color: '#A9DACF', name: '21-40%' },
  { from: 41, to: 60, color: '#79C4B3', name: '41-60%' },
  { from: 61, to: 80, color: '#46A991', name: '61-80%' },
  { from: 81, to: 100, color: '#148F77', name: '81-100%' },
];

export function UsageHeatmap({ title, subtitle, series, height, action, footer }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography component='h2' variant='h6' color='primary'>{title}</Typography>
          <div style={{ flex: 1 }} />
          {action}
        </div>
        {subtitle && (
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 4 }}>{subtitle}</Typography>
        )}
        <Chart
          type='heatmap'
          // Default: 39px per row plus the ~92px of chrome Apex reserves for the top
          // axis and legend (measured, see UsageCompareTab). No large floor — Apex
          // spreads the plot area over however many rows exist, so padding out the
          // height of a 2-row chart just yields two fat bands.
          height={height || (Math.max(series.length, 1) * 39 + 92)}
          options={{
            chart: { toolbar: { show: false }, animations: { enabled: false } },
            dataLabels: { enabled: false },
            colors: ['#148F77'],
            xaxis: { position: 'top' },
            plotOptions: {
              heatmap: {
                radius: 2,
                enableShades: false,
                colorScale: { ranges: INTENSITY_RANGES },
              },
            },
            legend: {
              show: true,
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '12px',
              markers: { radius: 2, width: 14, height: 14 },
              itemMargin: { horizontal: 8, vertical: 4 },
            },
            tooltip: {
              custom: ({ seriesIndex, dataPointIndex, w }) => {
                const cell = w.config.series[seriesIndex].data[dataPointIndex];
                const row = w.config.series[seriesIndex].name;
                const raw = cell.raw != null ? cell.raw : cell.y;
                return (
                  '<div style="padding:6px 10px">' +
                  `<strong>${row}</strong><br/>` +
                  `${cell.x}: ${raw.toLocaleString()}<br/>` +
                  `<span style="color:#777">${cell.y}% of column max</span>` +
                  '</div>'
                );
              },
            },
          }}
          series={series}
        />
        {footer}
      </CardContent>
    </Card>
  );
}

UsageHeatmap.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  series: PropTypes.array.isRequired,
  height: PropTypes.number,
  action: PropTypes.node,
  footer: PropTypes.node,
};
